import React from "react";
import { AlertTriangle } from "lucide-react";

/**
 * StagingBanner — always-visible amber ribbon shown on the staging
 * environment so the team can't accidentally treat it as production.
 *
 * Detection (cheapest signal first):
 *   1. import.meta.env.VITE_APP_ENV === "staging" — the canonical
 *      switch. Set on the staging Vercel project's env vars.
 *   2. Hostname includes "staging" — fallback so the ribbon still
 *      appears on *-staging-*.vercel.app even before VITE_APP_ENV
 *      is wired, and so the local dev never accidentally renders it.
 *
 * Returns null on prod so it has zero runtime cost for the 99% of
 * traffic that isn't staging. Static positioning (not fixed) so it
 * pushes page content down — using sticky/fixed risked covering
 * top-pinned modals on dashboard pages.
 */
export default function StagingBanner() {
  const envFlag = import.meta.env.VITE_APP_ENV;
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  const isStaging =
    envFlag === "staging" || /staging/i.test(hostname);

  if (!isStaging) return null;

  return (
    <div
      role="status"
      aria-label="Staging environment notice"
      className="w-full bg-amber-500 text-black border-b border-amber-700/40"
    >
      <div className="max-w-7xl mx-auto px-4 py-1.5 flex items-center justify-center gap-2 text-xs sm:text-sm font-bold tracking-wide">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <span className="uppercase">Staging environment</span>
        <span className="hidden sm:inline font-normal text-black/75 normal-case">
          — for internal testing only. Data here is not production data.
        </span>
      </div>
    </div>
  );
}
